/* Source: bogos-adapter.js */
/*
 * Retouch campaign / BOGOS adapter, 2026-10-21.
 *
 * This is a CUSTOM ADAPTER, not a supported BOGOS SDK API. Since 2026-10-26 it
 * accepts any single BOGOS gift build >= 239 (freegifts-241's gift.min.js is
 * byte-identical to 239). Static source inspection of the public storefront assets established:
 * https://cdn.shopify.com/extensions/01a15c9b-7ec1-707f-8c65-e4416b1ff74c/freegifts-239/assets/gift.min.js
 * https://static.bogos.io/cmV0b3VjaC1lY29tLm15c2hvcGlmeS5jb20=/freegifts_data_1792571469.min.js
 *
 * - The core creates FGSECOMAPP before dispatching fg-app:start.
 * - Gift initialization replaces FGSECOMAPP.offers with a processed array.
 * - Each gift pass reads that property before selecting eligible gifts.
 * - bogos:gifts with no cart payload fetches the native cart and recalculates.
 * - Native original-product gift markers are GF_<offer_root>, type "gift".
 *
 * We retain the FULL processed array on every assignment and filter only the
 * three configured gift offers when the caller requests suppression. We do
 * not mutate fgData, global gift settings, fetch, or XMLHttpRequest. The
 * source-verified native discount-code setter is used only to synchronize an
 * authoritative code list before restoration; no storage keys are written directly.
 * The caller owns coupon detection, cart mutations, drawer rendering, and
 * shipping. This adapter never removes/adds a cart line or applies a coupon.
 * Optional isCampaignCart(cart) must be pure. It checks the current native
 * cart at the offers getter, covering first paint before the caller's GET
 * /cart.js completes. Explicit suppression still wins during a mutation.
 *
 * Load before the BOGOS assets where possible. A controller must inspect the
 * returned readiness state and surface failures; unsupported versions fail
 * OPEN (original BOGOS behavior), never by disabling every gift in the shop.
 * This does not enforce rules inside Shopify checkout.
 */
(function expose(root, factory) {
  "use strict";
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.RetouchBogosCampaignAdapter = api;
})(typeof window === "object" ? window : globalThis, function buildModule() {
  "use strict";

  var OFFER_IDS = Object.freeze(["212022", "212023", "212024"]);
  // 2026-10-26: no longer pinned to one build. BOGOS bumps its app-extension
  // version on its own (freegifts-239 -> 241 on 25/10 with a byte-identical
  // gift.min.js), and the exact-URL pin then blocked every EFTERÅR15 cart.
  // Any single BOGOS gift build from Shopify's extension CDN (>= the inspected
  // 239) and this shop's core file are accepted; the native-interface checks
  // below remain the real guard.
  var GIFT_ASSET_PATTERN = /^https:\/\/cdn\.shopify\.com\/extensions\/[0-9a-f-]+\/freegifts-(\d+)\/assets\/gift\.min\.js$/;
  var CORE_PATTERN = /^https:\/\/static\.bogos\.io\/cmV0b3VjaC1lY29tLm15c2hvcGlmeS5jb20=\/freegifts_data_\d+\.min\.js$/;
  var MIN_SOURCE_VERSION = 239;
  var SOURCE_VERSION = "freegifts-" + MIN_SOURCE_VERSION + "+";

  function offerRoot(offer) {
    return String(offer && (offer.offer_root == null ? offer.id : offer.offer_root));
  }

  function scriptSources(doc, pattern) {
    var seen = [];
    Array.from(doc.querySelectorAll("script[src]"))
      .map(function (node) { return String(node.src || node.getAttribute("src") || "").split(/[?#]/)[0]; })
      .filter(function (src) { return pattern.test(src); })
      .forEach(function (src) { if (!seen.includes(src)) seen.push(src); });
    return seen;
  }

  function giftAssetVersion(src) {
    var match = GIFT_ASSET_PATTERN.exec(src);
    var version = match ? Number(match[1]) : NaN;
    return version >= MIN_SOURCE_VERSION ? version : null;
  }

  function create(options) {
    options = options || {};
    var win = options.window;
    var doc = options.document || (win && win.document);
    if (!win || !doc || typeof doc.addEventListener !== "function") {
      throw new TypeError("BOGOS adapter requires window and document.");
    }

    var suppressed = false;
    var destroyed = false;
    var current = null;
    var bindings = [];
    var listeners = [];
    var cartBusy = false;
    var giftBusy = false;
    var recomputeQueued = false;
    var lastNotification = "";
    var status = "waiting";
    var error = null;
    var verifiedSource = null;

    function suppressionActive() {
      if (destroyed || status !== "ready") return false;
      if (suppressed) return true;
      if (typeof options.isCampaignCart !== "function" || !current || current.app !== win.FGSECOMAPP) return false;
      try { return options.isCampaignCart(current.app.SHOPIFY_CART) === true; }
      catch (predicateError) {
        // Preserve native offers if the caller's pure predicate is broken.
        // No notification is dispatched from the getter (avoids reentrancy).
        status = "unsupported";
        error = "campaign-cart-predicate-failed";
        recomputeQueued = false;
        return false;
      }
    }

    function state() {
      var effectiveSuppression = suppressionActive();
      return {
        ready: status === "ready",
        status: status,
        error: error,
        installed: Boolean(current),
        suppressed: suppressed,
        effectiveSuppression: effectiveSuppression,
        busy: cartBusy || giftBusy,
        recomputeQueued: recomputeQueued,
        sourceVersion: verifiedSource ? "freegifts-" + giftAssetVersion(verifiedSource) : SOURCE_VERSION,
        sourceVerified: Boolean(verifiedSource),
        offerIds: OFFER_IDS.slice()
      };
    }

    function notify() {
      var snapshot = state();
      var signature = JSON.stringify(snapshot);
      if (signature === lastNotification) return;
      lastNotification = signature;
      if (typeof options.onStateChange === "function") {
        // A controller error must not escape into a third-party native setter.
        try { options.onStateChange(snapshot); }
        catch (callbackError) {
          if (win.console && typeof win.console.error === "function") {
            win.console.error("Retouch BOGOS state callback failed", callbackError);
          }
        }
      }
    }

    function sourceStatus(app) {
      var sources = scriptSources(doc, /\/freegifts-[^/]+\/assets\/gift\.min\.js$/);
      var nativeReady = Boolean(app.helper && typeof app.helper.addGiftToCart === "function");
      // Two different builds on one page, or one from an unknown origin, is not a state we inspected.
      if (sources.length > 1 || sources.some(function (src) { return giftAssetVersion(src) === null; })) {
        return { status: "unsupported", error: "unsupported-gift-script-version" };
      }
      if (!sources.length) {
        return { status: nativeReady ? "unsupported" : "waiting", error: nativeReady ? "gift-script-not-verifiable" : null };
      }
      if (!nativeReady) return { status: "waiting", error: null };
      if (!app.fgSettings || app.fgSettings.offer_process !== "function") {
        return { status: "unsupported", error: "unsupported-gift-engine" };
      }
      return { status: "ready", error: null, source: sources[0] };
    }

    function hook(app) {
      var existing = bindings.find(function (binding) { return binding.app === app; });
      var descriptor = Object.getOwnPropertyDescriptor(app, "offers");
      if (existing) {
        if (descriptor && descriptor.get === existing.get && descriptor.set === existing.set) {
          current = existing;
          return true;
        }
        error = "offers-hook-replaced";
        return false;
      }
      if (!descriptor || !descriptor.configurable || !("value" in descriptor) || !descriptor.writable) {
        error = "offers-property-not-configurable-data";
        return false;
      }
      if (!Array.isArray(descriptor.value)) {
        error = "offers-value-not-array";
        return false;
      }
      var binding = { app: app, descriptor: descriptor, full: descriptor.value };
      binding.get = function () {
        if (destroyed || binding !== current || app !== win.FGSECOMAPP || !suppressionActive()) {
          return binding.full;
        }
        return binding.full.filter(function (offer) { return !OFFER_IDS.includes(offerRoot(offer)); });
      };
      binding.set = function (value) {
        // Never coerce or discard a replacement supplied by native BOGOS.
        binding.full = value;
        if (!Array.isArray(value)) {
          status = "unsupported";
          error = "offers-value-not-array";
          recomputeQueued = false;
          notify();
        }
      };
      try {
        Object.defineProperty(app, "offers", {
          configurable: true,
          enumerable: descriptor.enumerable,
          get: binding.get,
          set: binding.set
        });
      } catch (defineError) {
        error = "offers-hook-install-failed";
        return false;
      }
      bindings.push(binding);
      current = binding;
      return true;
    }

    function refresh() {
      if (destroyed) return state();
      var app = win.FGSECOMAPP;
      if (!app || typeof app !== "object") {
        current = null;
        verifiedSource = null;
        status = "waiting";
        error = null;
        notify();
        return state();
      }
      if (!hook(app)) {
        status = "unsupported";
        verifiedSource = null;
        recomputeQueued = false;
        notify();
        return state();
      }
      var result = sourceStatus(app);
      if (!Array.isArray(current.full)) result = { status: "unsupported", error: "offers-value-not-array" };
      status = result.status;
      error = result.error;
      verifiedSource = result.source || null;
      notify();
      return state();
    }

    function dispatchRecompute() {
      var snapshot = refresh();
      if (!snapshot.ready) return { scheduled: false, reason: snapshot.error || snapshot.status, state: snapshot };
      if (cartBusy || giftBusy) {
        recomputeQueued = true;
        notify();
        return { scheduled: true, reason: "waiting-for-native-pass", state: state() };
      }
      recomputeQueued = false;
      if (typeof win.CustomEvent !== "function") {
        status = "unsupported";
        error = "custom-event-unavailable";
        notify();
        return { scheduled: false, reason: error, state: state() };
      }
      doc.dispatchEvent(new win.CustomEvent("bogos:gifts", {
        detail: { event: "retouch:campaign-recompute" }
      }));
      notify();
      return { scheduled: true, reason: "native-event-dispatched", state: state() };
    }

    function listen(name, handler) {
      doc.addEventListener(name, handler);
      listeners.push([name, handler]);
    }

    function endPass(kind) {
      if (kind === "gift") giftBusy = false;
      else cartBusy = false;
      refresh();
      if (recomputeQueued && !cartBusy && !giftBusy) dispatchRecompute();
    }

    listen("fg-app:start", refresh);
    listen("fg-app:end", refresh);
    listen("bogos:gifts-start", function () { giftBusy = true; refresh(); });
    listen("bogos:gifts-end", function () { endPass("gift"); });
    listen("fg-cart:start-process", function () { cartBusy = true; refresh(); });
    listen("fg-cart:end-process", function () { endPass("cart"); });
    refresh();

    return Object.freeze({
      getState: state,
      refresh: refresh,
      setSuppressed: function (value) {
        if (typeof value !== "boolean") throw new TypeError("Suppression must be boolean.");
        if (destroyed) return state();
        suppressed = value;
        return refresh();
      },
      recompute: dispatchRecompute,
      syncDiscountCodes: function (cart) {
        // The verified core merges this native code cache into gift updates.
        // A removed coupon must not survive there and be reapplied by BOGOS.
        var snapshot = refresh();
        var app = win.FGSECOMAPP;
        var nativeCodes = app && app.helper && app.helper.shopifyFunc && app.helper.shopifyFunc.discount_codes;
        // The core file name changes whenever BOGOS republishes the shop data, so only its origin and shape are checked.
        var coreSources = scriptSources(doc, /\/freegifts_data_[^/]+\.min\.js$/);
        if (!snapshot.ready || coreSources.length !== 1 || !CORE_PATTERN.test(coreSources[0]) ||
            !nativeCodes || typeof nativeCodes.get !== "function" || typeof nativeCodes.set !== "function" ||
            !app.CUSTOM_EVENTS || app.CUSTOM_EVENTS.BOGOS_CART_CHANGE !== "bogos:cart-change" || typeof win.CustomEvent !== "function") {
          throw new Error("Gavefunktionens rabatkoder kunne ikke synkroniseres sikkert. Genindlæs kurven og prøv igen.");
        }
        if (!cart || !Array.isArray(cart.discount_codes) || cart.discount_codes.some(function (entry) {
          return !entry || typeof entry.code !== "string" || !entry.code.trim();
        })) throw new Error("Kurvens fulde rabatkoder kunne ikke bekræftes. Genindlæs kurven og prøv igen.");
        // Preserve every authoritative code, including applicable:false.
        var codes = cart.discount_codes.map(function (entry) { return entry.code; });
        nativeCodes.set(codes.slice());
        var stored = nativeCodes.get();
        if (!Array.isArray(stored) || JSON.stringify(stored) !== JSON.stringify(codes)) {
          throw new Error("Gavefunktionens rabatkoder kunne ikke gemmes sikkert. Prøv igen, før du går til betaling.");
        }
        // Invalidate the native 500 ms cart/full-cart cache before recompute.
        doc.dispatchEvent(new win.CustomEvent("bogos:cart-change"));
        return { synced: true, count: codes.length };
      },
      restoreAndRecompute: function () {
        if (destroyed) return { scheduled: false, reason: "destroyed", state: state() };
        suppressed = false;
        return dispatchRecompute();
      },
      isTargetGift: function (line) {
        if (!line || !current || !Array.isArray(current.full) || status !== "ready") return false;
        var properties = Array.isArray(line.properties)
          ? Object.fromEntries(line.properties.map(function (entry) { return [entry.key, entry.value]; }))
          : (line.properties || {});
        var id = String(properties._bogos_trigger_id || "");
        if (!OFFER_IDS.includes(id) || properties._bogos_trigger_type !== "gift" || properties._bogos_trigger !== "GF_" + id) return false;
        return current.full.some(function (offer) {
          if (offerRoot(offer) !== id || !Array.isArray(offer.gift_items)) return false;
          return offer.gift_items.some(function (gift) {
            var variant = gift.original_variant_shopify_id == null ? gift.variant_shopify_id : gift.original_variant_shopify_id;
            return variant != null && String(variant) === String(line.variant_id);
          });
        });
      },
      destroy: function () {
        if (destroyed) return state();
        destroyed = true;
        suppressed = false;
        recomputeQueued = false;
        listeners.forEach(function (entry) { doc.removeEventListener(entry[0], entry[1]); });
        var restoreError = null;
        bindings.forEach(function (binding) {
          var descriptor = Object.getOwnPropertyDescriptor(binding.app, "offers");
          // Do not overwrite a descriptor another integration subsequently set.
          if (descriptor && descriptor.get === binding.get && descriptor.set === binding.set) {
            try {
              Object.defineProperty(binding.app, "offers", Object.assign({}, binding.descriptor, { value: binding.full }));
            } catch (descriptorError) {
              // If another script froze our accessor, it remains a pass-through
              // because destroyed is already true. Do not throw into native code.
              restoreError = "offers-hook-restore-failed";
            }
          }
        });
        current = null;
        status = "destroyed";
        error = restoreError;
        verifiedSource = null;
        notify();
        return state();
      }
    });
  }

  return Object.freeze({ create: create, sourceVersion: SOURCE_VERSION, offerIds: OFFER_IDS });
});
;

/* Source: retouch-campaign-cart.js */
/*
 * EFTERÅR15 storefront mitigation, 2026-10-21. NOT a checkout validation rule.
 * Shopify Ajax cart/update.js supports discount codes + line-key updates.
 * https://shopify.dev/docs/api/ajax/reference/cart
 * This deliberately does not intercept fetch/XHR or poll the cart. The BOGOS
 * adapter must be source-verified and ready before any gift is removed.
 */
(function expose(root, factory) {
  "use strict";
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else {
    root.RetouchCampaignCart = api;
    if (!root.RetouchCampaignCartConfig || root.RetouchCampaignCartConfig.enabled !== false) {
      api.boot(root, root.document, root.RetouchCampaignCartConfig || {});
    }
  }
})(typeof window === "object" ? window : globalThis, function buildModule() {
  "use strict";
  var CODE = "EFTERÅR15";
  var OFFER_IDS = ["212022", "212023", "212024"];
  // Extended by Retouch on 2026-10-26 to Tuesday 27/10 23:59 Danish time (= discount endsAt in Shopify).
  var CAMPAIGN_END = Date.parse("2026-10-27T22:59:00Z");

  function campaignAvailable(now) {
    return (now == null ? Date.now() : Number(now)) <= CAMPAIGN_END;
  }
  function checkoutGateState(state, adapterState) {
    state = state || {};
    if (state.pending) return { blocked: true, message: "Vent et øjeblik. Rabatkoden og gaverne bliver opdateret, før du kan gå til betaling." };
    if (state.checkoutUncertain) return { blocked: true, message: "Ændringen af rabatkoden kunne ikke bekræftes. Prøv igen, eller genindlæs kurven, før du går til betaling." };
    if (!state.loaded) return { blocked: true, message: state.error || "Vi kontrollerer din kurv, før du kan gå til betaling…" };
    if (state.checking) return { blocked: true, message: "Vi kontrollerer rabatkoden og gaverne, før du kan gå til betaling…" };
    if (state.active && state.error) return { blocked: true, message: state.error + " Genindlæs kurven, eller fjern EFTERÅR15, før du går til betaling." };
    if (state.active && !(adapterState && adapterState.ready)) return { blocked: true, message: "Gavefunktionen kunne ikke kontrolleres sikkert. Vent et øjeblik, eller genindlæs kurven, før du går til betaling." };
    return { blocked: false, message: "" };
  }
  function campaignPanelVisible(state, now) {
    return campaignAvailable(now) || Boolean(state.active || state.pending || state.checkoutUncertain || (!state.loaded && state.error));
  }
  function createCheckoutGate(options) {
    var doc = options.document;
    var win = options.window;
    var locked = new Map();
    var controlSelector = 'button[name="checkout"], input[name="checkout"], #checkout, #CartDrawer-Checkout, a[href]';
    function isCheckoutURL(value) {
      if (!value) return false;
      try {
        var parsed = new win.URL(value, win.location.href);
        return parsed.origin === win.location.origin && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout(?:\/|$)/i.test(parsed.pathname);
      } catch (error) { return false; }
    }
    function isCheckoutControl(node) {
      if (!node || typeof node.matches !== "function") return false;
      return node.matches('button[name="checkout"], input[name="checkout"], #checkout, #CartDrawer-Checkout') ||
        (node.matches('a[href]') && isCheckoutURL(node.getAttribute("href")));
    }
    function state() { return checkoutGateState(options.getState(), options.getAdapterState()); }
    function unlock(node, previous) {
      node.removeAttribute("data-rt-checkout-locked");
      // Never set native `disabled`: another app may own it (for example the
      // terms checkbox). Restoring our gate must not enable its locked button.
      if (node.getAttribute("aria-disabled") === "true") {
        if (previous == null) node.removeAttribute("aria-disabled");
        else node.setAttribute("aria-disabled", previous);
      }
    }
    function update() {
      var snapshot = state();
      locked.forEach(function (previous, node) {
        if (!snapshot.blocked || !node.isConnected) { unlock(node, previous); locked.delete(node); }
      });
      if (snapshot.blocked) doc.querySelectorAll(controlSelector).forEach(function (node) {
        if (!isCheckoutControl(node)) return;
        if (!locked.has(node)) locked.set(node, node.getAttribute("aria-disabled"));
        node.setAttribute("aria-disabled", "true");
        node.setAttribute("data-rt-checkout-locked", "true");
      });
      return snapshot;
    }
    function prevent(event) {
      var snapshot = state();
      if (!snapshot.blocked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      update();
      if (options.onBlocked) options.onBlocked(snapshot.message);
    }
    function click(event) {
      var control = event.target && event.target.closest && event.target.closest(controlSelector);
      if (isCheckoutControl(control)) prevent(event);
    }
    function submit(event) {
      if (isCheckoutControl(event.submitter) || (event.target && isCheckoutURL(event.target.getAttribute("action")))) prevent(event);
    }
    doc.addEventListener("click", click, true);
    doc.addEventListener("submit", submit, true);
    return { update: update, destroy: function () {
      doc.removeEventListener("click", click, true);
      doc.removeEventListener("submit", submit, true);
      locked.forEach(function (previous, node) { unlock(node, previous); });
      locked.clear();
    } };
  }

  function normalize(value) { return String(value || "").trim().normalize("NFC").toUpperCase(); }
  function previewDiagnosticsEnabled(win, config) {
    return Boolean(config && config.debug === true && win.Shopify && win.Shopify.theme &&
      ["unpublished", "development"].includes(win.Shopify.theme.role));
  }
  function createPreviewDiagnostics(win, config) {
    config = config || {};
    var sequence = 0;
    function safeCode(value) {
      var code = normalize(value);
      return code === CODE || (config.testCode && code === normalize(config.testCode)) ? code : "[other code]";
    }
    function cartSummary(cart) {
      if (!cart || !Array.isArray(cart.items)) return null;
      return {
        discount_codes: Array.isArray(cart.discount_codes) ? cart.discount_codes.map(function (entry) {
          return { code: safeCode(entry.code), applicable: entry.applicable === true };
        }) : null,
        total_price: typeof cart.total_price === "number" ? cart.total_price : null,
        items: cart.items.map(function (item) {
          var properties = Array.isArray(item.properties)
            ? Object.fromEntries(item.properties.map(function (entry) { return [entry.key, entry.value]; }))
            : (item.properties || {});
          return { title: String(item.title || "").slice(0, 200), quantity: Number(item.quantity), gift: properties._bogos_trigger_type === "gift" };
        })
      };
    }
    return function diagnostic(event, details) {
      if (!previewDiagnosticsEnabled(win, config) || !win.console || typeof win.console.info !== "function") return;
      try {
        details = details || {};
        var entry = { event: event, sequence: ++sequence };
        if (typeof details.path === "string") entry.path = details.path.split(/[?#]/)[0];
        if (details.payload) {
          entry.discount_field_present = Object.prototype.hasOwnProperty.call(details.payload, "discount");
          if (entry.discount_field_present) entry.discount = String(details.payload.discount).split(",").filter(Boolean).map(safeCode).join(",");
        }
        if (details.cart) entry.cart = cartSummary(details.cart);
        if (details.cachedCart) entry.cached_cart = cartSummary(details.cachedCart);
        if (details.state) entry.state = {
          active: Boolean(details.state.active), pending: Boolean(details.state.pending), checking: Boolean(details.state.checking),
          error: String(details.state.error || "").slice(0, 300)
        };
        if (typeof details.reason === "string") entry.reason = details.reason;
        if (typeof details.restore === "boolean") entry.restore = details.restore;
        win.console.info("[Retouch campaign preview] " + JSON.stringify(entry));
      } catch (error) { /* Diagnostics must never affect storefront behavior. */ }
    };
  }
  function storefrontEnvironment(win, config) {
    // This bundle intentionally executes before content_for_header. Shopify
    // can define its theme/locale globals later, so never cache them at boot.
    var shopify = win.Shopify || {};
    var preview = Boolean(shopify.theme && ["unpublished", "development", "demo"].includes(shopify.theme.role));
    var allowed = [CODE];
    if (preview && config && config.testCode) allowed.push(normalize(config.testCode));
    var routeRoot = shopify.routes && shopify.routes.root;
    if (typeof routeRoot !== "string" || !/^\/(?!\/)[^\s\\?#]*$/.test(routeRoot)) routeRoot = "/";
    if (!routeRoot.endsWith("/")) routeRoot += "/";
    return { allowedCodes: allowed, root: routeRoot };
  }
  function allocations(cart) {
    var applications = (cart.cart_level_discount_applications || []).slice();
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (allocation) {
        if (allocation.discount_application) applications.push(allocation.discount_application);
      });
    });
    return applications.filter(function (application) { return application.type === "discount_code"; });
  }
  function activeCode(cart, allowed) {
    allowed = allowed || [CODE];
    var candidates = Array.isArray(cart.discount_codes)
      ? cart.discount_codes.filter(function (item) { return item.applicable === true; }).map(function (item) { return item.code; })
      : allocations(cart).map(function (item) { return item.code || item.title; });
    return candidates.find(function (code) { return allowed.includes(normalize(code)); }) || null;
  }
  function existingCodes(cart) {
    // Allocation fallbacks cannot reveal all shipping/inapplicable codes. Do
    // not overwrite a customer's full code list from that incomplete view.
    if (!Array.isArray(cart.discount_codes)) throw new Error("Kurvens rabatkoder kunne ikke aflæses sikkert. Prøv at genindlæse siden.");
    var seen = new Set();
    return cart.discount_codes.map(function (item) { return String(item.code || "").trim(); })
      .filter(function (code) {
        var key = normalize(code);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }
  function giftUpdates(cart, predicate) {
    var updates = {};
    (cart.items || []).forEach(function (item) {
      if (predicate(item)) {
        if (typeof item.key !== "string" || !item.key) throw new Error("En gave kunne ikke identificeres sikkert. Genindlæs kurven.");
        updates[item.key] = 0;
      }
    });
    return updates;
  }
  function hasUnresolvedGift(cart, predicate) {
    return (cart.items || []).some(function (item) {
      var properties = Array.isArray(item.properties)
        ? Object.fromEntries(item.properties.map(function (entry) { return [entry.key, entry.value]; }))
        : (item.properties || {});
      var id = String(properties._bogos_trigger_id || "");
      return OFFER_IDS.includes(id) && properties._bogos_trigger === "GF_" + id &&
        properties._bogos_trigger_type === "gift" && !predicate(item);
    });
  }
  function applyPayload(cart, code, predicate) {
    return {
      discount: existingCodes(cart).filter(function (value) { return normalize(value) !== normalize(code); }).concat([normalize(code)]).join(","),
      updates: giftUpdates(cart, predicate)
    };
  }
  function removePayload(cart, code) {
    return { discount: existingCodes(cart).filter(function (value) { return normalize(value) !== normalize(code); }).join(",") };
  }
  function observeNativeRestoration(doc, win, timeoutMs) {
    var started = false;
    var settled = false;
    var resolve;
    var reject;
    var promise = new Promise(function (done, fail) { resolve = done; reject = fail; });
    function matches(event) {
      // `events` is BOGOS's accumulated history, not the current pass. The
      // source-verified current pass is detail.event, a one-element array.
      return event.detail && Array.isArray(event.detail.event) && event.detail.event[0] === "retouch:campaign-recompute";
    }
    function finish(error) {
      if (settled) return;
      settled = true;
      doc.removeEventListener("fg-cart:start-process", start);
      doc.removeEventListener("fg-cart:end-process", end);
      win.clearTimeout(timeout);
      if (error) reject(error); else resolve();
    }
    function start(event) { if (matches(event)) started = true; }
    function end(event) { if (started && matches(event)) finish(); }
    doc.addEventListener("fg-cart:start-process", start);
    doc.addEventListener("fg-cart:end-process", end);
    var timeout = win.setTimeout(function () {
      finish(new Error("Gaverne blev ikke færdigberegnet. Genindlæs kurven og kontrollér varerne, før du fortsætter."));
    }, timeoutMs || 12000);
    return { promise: promise, cancel: function () { finish(); } };
  }
  async function completeRestoration(options) {
    // Observe BEFORE dispatch: BOGOS debounces this event by 250 ms and is
    // not busy yet when dispatch returns. Waiting only for idle is too early.
    var observer = options.observe();
    try {
      if (options.onStage) options.onStage("dispatch");
      var result = options.adapter.restoreAndRecompute();
      if (!result || result.scheduled !== true) {
        throw new Error("Gaverne kunne ikke beregnes igen. Genindlæs kurven og kontrollér varerne, før du fortsætter.");
      }
      await observer.promise;
      if (options.onStage) options.onStage("native-complete");
      await options.waitIdle();
      if (options.onStage) options.onStage("native-idle");
      // A request from the previous document can finish after this document's
      // HTML was rendered. Native BOGOS then sees no cart change and may skip
      // its DOM render. Refresh exactly once even in that no-change case.
      var cart = await options.read();
      if (options.onStage) options.onStage("read-complete", cart);
      await options.render(cart);
      if (options.onStage) options.onStage("render-complete", cart);
      return cart;
    } finally {
      observer.cancel();
    }
  }

  function createController(options) {
    var api = options.api;
    var adapter = options.adapter;
    function allowedCodes() {
      return (options.getAllowedCodes ? options.getAllowedCodes() : (options.allowedCodes || [CODE])).map(normalize);
    }
    var waitIdle = options.waitIdle || function () { return Promise.resolve(); };
    var current = { loaded: false, pending: false, checking: false, checkoutUncertain: false, active: false, code: null, error: "", cart: null };
    var chain = Promise.resolve();
    var destroyed = false;
    var reconcileError = false;
    var discountSyncPending = false;
    function emit(change) {
      current = Object.assign({}, current, change);
      if (!destroyed && options.onState) options.onState(Object.assign({}, current));
    }
    function serial(action) {
      var next = chain.then(function () { if (!destroyed) return action(); });
      chain = next.catch(function () {});
      return next;
    }
    function requireAdapter() {
      var status = adapter.refresh ? adapter.refresh() : adapter.getState();
      if (!status.ready) throw new Error("Gavefunktionen er ikke klar. Vent et øjeblik og prøv igen; kurven er ikke ændret.");
      return status;
    }
    function adopt(cart) {
      var code = activeCode(cart, allowedCodes());
      emit({ cart: cart, loaded: true, checkoutUncertain: false, active: Boolean(code), code: code });
      adapter.setSuppressed(Boolean(code));
      return code;
    }
    function adoptAfterDiscountSync(cart) {
      if (discountSyncPending && !activeCode(cart, allowedCodes())) {
        var synced = adapter.syncDiscountCodes && adapter.syncDiscountCodes(cart);
        if (!synced || synced.synced !== true) throw new Error("Gavefunktionens rabatkoder kunne ikke synkroniseres sikkert. Genindlæs kurven og prøv igen.");
        discountSyncPending = false;
        if (options.onDiagnostic) options.onDiagnostic("restore.codes-synced", { cart: cart, state: current });
      }
      return adopt(cart);
    }
    function restoreGifts() {
      var result = adapter.restoreAndRecompute();
      if (options.onDiagnostic) options.onDiagnostic("restore.dispatch", { state: current, reason: result && result.reason });
      if (!result || result.scheduled !== true) {
        throw new Error("Gaverne kunne ikke beregnes igen. Genindlæs kurven og kontrollér varerne, før du fortsætter.");
      }
    }
    function requireKnownGifts(cart) {
      if (hasUnresolvedGift(cart, adapter.isTargetGift)) {
        throw new Error("En kampagnegave kunne ikke identificeres sikkert. Den er ikke fjernet. Kontrollér kurven, før du fortsætter.");
      }
    }
    async function recover(adoptRecovered) {
      try {
        var cart = await api.read();
        var code = adoptRecovered ? adoptRecovered(cart) : adopt(cart);
        if (!code) restoreGifts();
      } catch (error) {
        // Preserve the last authoritative state, not a guess about an HTTP
        // mutation whose outcome is unknown. A reload will re-read Shopify.
        adapter.setSuppressed(current.active || current.checkoutUncertain);
      }
    }
    async function clearRemainingGifts(cart) {
      requireKnownGifts(cart);
      var updates = giftUpdates(cart, adapter.isTargetGift);
      if (!Object.keys(updates).length) return cart;
      await waitIdle();
      cart = await api.read();
      if (!activeCode(cart, allowedCodes())) return cart;
      requireKnownGifts(cart);
      updates = giftUpdates(cart, adapter.isTargetGift);
      if (Object.keys(updates).length) cart = await api.write({ updates: updates });
      if (Object.keys(giftUpdates(cart, adapter.isTargetGift)).length) {
        throw new Error("Gaverne kunne ikke fjernes helt. Genindlæs kurven og kontrollér varerne, før du fortsætter.");
      }
      return cart;
    }
    async function mutation(kind, suppliedCode) {
      var wrote = false;
      var syncFailed = false;
      function adoptMutationCart(cart) {
        // Do not release suppression before the native cache is verified.
        try { var code = adoptAfterDiscountSync(cart); syncFailed = false; return code; }
        catch (error) { syncFailed = discountSyncPending; throw error; }
      }
      reconcileError = false;
      emit({ pending: true, error: "" });
      try {
        requireAdapter();
        if (kind === "apply") adapter.setSuppressed(true);
        await waitIdle();
        var cart = await api.read();
        var target = kind === "apply" ? normalize(suppliedCode) : (activeCode(cart, allowedCodes()) || current.code);
        if (!target) {
          var restorePending = discountSyncPending;
          adoptMutationCart(cart);
          if (restorePending && !current.active) restoreGifts();
          return;
        }
        if (kind === "apply") requireKnownGifts(cart);
        var payload = kind === "apply" ? applyPayload(cart, target, adapter.isTargetGift) : removePayload(cart, target);
        if (kind === "remove") discountSyncPending = true;
        wrote = true; // HTTP outcome may be uncertain even if response rejects.
        cart = await api.write(payload);
        var code = activeCode(cart, allowedCodes());
        if (kind === "apply" && normalize(code) !== normalize(target)) {
          // Remove only the attempted, unusable campaign code; preserve every
          // unrelated code in the fresh response. Native BOGOS restores gifts.
          discountSyncPending = true;
          cart = await api.write(removePayload(cart, target));
          adoptMutationCart(cart);
          if (!current.active) restoreGifts();
          throw new Error("Koden kan ikke bruges til denne kurv lige nu. Gaverne bliver beregnet igen.");
        }
        code = adoptMutationCart(cart);
        if (code) {
          requireAdapter();
          cart = await clearRemainingGifts(cart);
          adopt(cart);
        } else restoreGifts();
        if (kind === "remove" && normalize(activeCode(cart, allowedCodes())) === normalize(target)) {
          throw new Error("Koden blev ikke fjernet. Prøv igen.");
        }
      } catch (error) {
        // A successful server-side POST can lose its response. Until a fresh
        // read succeeds, an old inactive snapshot cannot unlock checkout.
        if (wrote) emit({ checkoutUncertain: true });
        await recover(adoptMutationCart);
        emit({ error: error.message || "Kurven kunne ikke opdateres. Prøv igen." });
      } finally {
        emit({ pending: false });
        if (wrote && options.onMutation) options.onMutation({ reason: kind, state: Object.assign({}, current), syncFailed: syncFailed || discountSyncPending });
      }
      return Object.assign({}, current);
    }
    return {
      getState: function () { return Object.assign({}, current); },
      setFeedback: function (message) { reconcileError = false; emit({ error: String(message || "") }); },
      apply: function (code) {
        if (!allowedCodes().includes(normalize(code))) {
          emit({ error: "Her kan du aktivere EFTERÅR15. Andre rabatkoder kan bruges ved betaling." });
          return Promise.resolve(Object.assign({}, current));
        }
        emit({ pending: true, error: "" });
        return serial(function () { return mutation("apply", code); });
      },
      remove: function () { emit({ pending: true, error: "" }); return serial(function () { return mutation("remove"); }); },
      reconcile: function () {
        return serial(async function () {
          emit({ checking: true });
          try {
            var before = current.active;
            var shouldRestore = before || discountSyncPending;
            var cart = await api.read();
            if ((before || discountSyncPending) && !activeCode(cart, allowedCodes())) {
              discountSyncPending = true;
              // Never replace a native pass's newly requested codes with a
              // stale response read before that pass finished.
              await waitIdle();
              cart = await api.read();
            }
            var code = adoptAfterDiscountSync(cart);
            if (code) {
              requireAdapter();
              var hadGifts = Object.keys(giftUpdates(cart, adapter.isTargetGift)).length > 0;
              cart = await clearRemainingGifts(cart);
              adopt(cart);
              if (hadGifts && options.onMutation) options.onMutation({ reason: "reconcile", state: Object.assign({}, current) });
            } else if (shouldRestore) restoreGifts();
            if (reconcileError) { reconcileError = false; emit({ error: "" }); }
          } catch (error) { reconcileError = true; emit({ checkoutUncertain: current.checkoutUncertain || discountSyncPending, error: error.message || "Kurven kunne ikke kontrolleres. Prøv igen." }); }
          finally { emit({ checking: false }); }
          return Object.assign({}, current);
        });
      },
      destroy: function () { destroyed = true; }
    };
  }

  function boot(win, doc, config) {
    config = config || {};
    if (win.__retouchCampaignCart) return win.__retouchCampaignCart;
    if (!win.RetouchBogosCampaignAdapter) {
      if (win.console) win.console.error("Retouch campaign adapter is missing; no cart changes made.");
      return null;
    }
    function environment() { return storefrontEnvironment(win, config); }
    var diagnostic = createPreviewDiagnostics(win, config);
    var flashKey = "rt-campaign-cart-feedback-v1";
    var controller;
    var scheduled = null;
    var nodes = new Set();
    var listeners = [];
    var flash = null;
    var restoreAfterBoot = false;
    var booted = false;
    var unsubscriber = null;
    var observer = null;
    var reloadRequested = false;
    var panelCount = 0;
    var checkoutGate = null;
    var expiryTimer = null;
    try { flash = JSON.parse(win.sessionStorage.getItem(flashKey)); win.sessionStorage.removeItem(flashKey); } catch (error) {}
    restoreAfterBoot = Boolean(flash && flash.restore);
    function listen(target, name, callback) { target.addEventListener(name, callback); listeners.push([target, name, callback]); }
    function schedule() {
      if (!booted || reloadRequested || scheduled) return;
      scheduled = win.setTimeout(function () { scheduled = null; controller.reconcile().then(restoreIfNeeded); }, 60);
    }
    var adapter = win.RetouchBogosCampaignAdapter.create({ window: win, document: doc, isCampaignCart: function (cart) { return Boolean(cart && activeCode(cart, environment().allowedCodes)); }, onStateChange: function (state) {
      if (controller) updateUI(controller.getState());
      if (state.ready && controller && !controller.getState().pending) schedule();
    } });
    async function restoreIfNeeded() {
      if (!restoreAfterBoot || !adapter.getState().ready || !controller.getState().loaded) return;
      diagnostic("restore.after-boot", { state: controller.getState(), restore: restoreAfterBoot });
      restoreAfterBoot = false;
      // Explicit apply/remove reloads can abort a native gift request started
      // just before navigation. Recompute once in the new document as well.
      // This does not replay saved gift items or alter entitlement/inventory.
      if (!controller.getState().active) {
        try {
          await completeRestoration({ adapter: adapter, waitIdle: waitIdle,
            observe: function () { return observeNativeRestoration(doc, win); },
            read: function () { return request("cart.js"); },
            onStage: function (stage, cart) { diagnostic("restore." + stage, { state: controller.getState(), cart: cart }); },
            render: function (cart) { if (!reloadRequested) return refreshNative(cart, true); }
          });
        } catch (error) { controller.setFeedback(error.message); diagnostic("restore.error", { state: controller.getState() }); }
      }
    }
    async function request(path, payload, sectionResponse) {
      diagnostic(payload ? "api.write.start" : "api.read.start", { path: path, payload: payload });
      var abort = new win.AbortController();
      var timeout = win.setTimeout(function () { abort.abort(); }, 12000);
      try {
        var response = await win.fetch(environment().root + path, {
          method: payload ? "POST" : "GET",
          credentials: "same-origin",
          cache: "no-store",
          signal: abort.signal,
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: payload ? JSON.stringify(payload) : undefined
        });
        var data = await response.json();
        if (!response.ok || !data || (sectionResponse ? typeof data !== "object" || Array.isArray(data) : !Array.isArray(data.items))) throw new Error("invalid-response");
        diagnostic(payload ? "api.write.result" : "api.read.result", { path: path, cart: data });
        return data;
      } catch (error) {
        diagnostic("api.error", { path: path });
        throw new Error("Forbindelsen til kurven kunne ikke bekræftes. Prøv igen eller genindlæs siden.");
      } finally { win.clearTimeout(timeout); }
    }
    function waitIdle() {
      if (!adapter.getState().busy) return Promise.resolve();
      return new Promise(function (resolve, reject) {
        var names = ["bogos:gifts-end", "fg-cart:end-process"];
        function cleanup() { names.forEach(function (name) { doc.removeEventListener(name, check); }); win.clearTimeout(timeout); }
        function check() { if (!adapter.getState().busy) { cleanup(); resolve(); } }
        var timeout = win.setTimeout(function () { cleanup(); reject(new Error("Kurven arbejder stadig. Vent et øjeblik og prøv igen.")); }, 6000);
        names.forEach(function (name) { doc.addEventListener(name, check); });
        check();
      });
    }
    function updateUI(state) {
      // Hide only this gift booster, never the separate shipping calculation.
      doc.documentElement.setAttribute("data-retouch-campaign-active", String(state.active && adapter.getState().ready));
      var gate = checkoutGateState(state, adapter.getState());
      var available = campaignAvailable();
      nodes.forEach(function (panel) {
        if (!panel.isConnected) { nodes.delete(panel); return; }
        panel.dataset.state = state.pending ? "loading" : state.error ? "error" : state.active ? "active" : "idle";
        panel.hidden = !campaignPanelVisible(state);
        panel.setAttribute("aria-busy", String(state.pending));
        var input = panel.querySelector("input");
        input.readOnly = state.pending;
        panel.querySelector("[data-rt-apply]").disabled = state.pending || !state.loaded || state.active;
        panel.querySelector("[data-rt-remove]").disabled = state.pending;
        panel.querySelector(".rt-campaign-cart__row").hidden = state.active || !available;
        panel.querySelector(".rt-campaign-cart__label").hidden = !available && !state.active;
        panel.querySelector(".rt-campaign-cart__help").hidden = !available && !state.active;
        panel.querySelector("[data-rt-remove]").hidden = !state.active;
        var message = gate.blocked ? gate.message : state.error || (state.active ? "EFTERÅR15 er aktiv. Gavetrappen er sat på pause, mens du bruger koden." : "Aktivér koden her. Du vælger 15 % rabat i stedet for gaver.");
        var status = panel.querySelector(".rt-campaign-cart__status");
        if (status.textContent !== message) status.textContent = message;
      });
      if (checkoutGate) checkoutGate.update();
    }
    async function refreshNative(cart, fetchSections) {
      var focusOrigin = doc.activeElement;
      var focusPanel = focusOrigin && focusOrigin.closest && focusOrigin.closest(".rt-campaign-cart");
      var drawerFocus = Boolean(focusPanel && focusPanel.closest("cart-drawer"));
      var footer = doc.getElementById("main-cart-footer");
      var renderSections = cart && cart.sections || {};
      if (fetchSections) {
        var ids = ["cart-icon-bubble"];
        if (footer && footer.dataset.id) ids.push(footer.dataset.id);
        renderSections = await request((footer ? "cart" : "") + "?sections=" + encodeURIComponent(ids.join(",")), undefined, true);
      }
      var renders = [];
      doc.querySelectorAll("cart-items, cart-drawer-items").forEach(function (element) {
        if (typeof element.onCartUpdate === "function") renders.push(Promise.resolve(element.onCartUpdate()));
      });
      var iconHTML = renderSections["cart-icon-bubble"];
      var icon = doc.getElementById("cart-icon-bubble");
      if (icon && typeof iconHTML === "string") {
        var iconDocument = new win.DOMParser().parseFromString(iconHTML, "text/html");
        var fragment = iconDocument.querySelector("#cart-icon-bubble") || iconDocument.querySelector(".shopify-section");
        if (fragment) icon.innerHTML = fragment.innerHTML;
      }
      // Dawn's CartItems.onCartUpdate refreshes the items section only. Keep
      // the main-cart totals in sync after our own automatic gift cleanup.
      // Also used once after our post-navigation restore, not as a listener
      // duplicating every native gift rendering pass.
      var footerHTML = footer && renderSections[footer.dataset.id];
      var footerTarget = footer && footer.querySelector(".js-contents");
      if (footerTarget && typeof footerHTML === "string") {
        var footerSource = new win.DOMParser().parseFromString(footerHTML, "text/html").querySelector(".js-contents");
        if (footerSource) footerTarget.innerHTML = footerSource.innerHTML;
      }
      await Promise.all(renders);
      // Section replacement must not strand keyboard users. Do not steal
      // focus back if they moved elsewhere while the request was in flight.
      if (focusPanel && !focusOrigin.isConnected && doc.activeElement === doc.body) {
        mount();
        var restoredPanel = doc.querySelector(drawerFocus ? "cart-drawer .rt-campaign-cart" : "#main-cart-footer .rt-campaign-cart");
        var restoredFocus = restoredPanel && restoredPanel.querySelector(focusOrigin.tagName === "INPUT" ? "input" : "[data-rt-remove]");
        if (restoredFocus && !restoredFocus.hidden) {
          if (focusOrigin.tagName === "INPUT") restoredFocus.value = focusOrigin.value;
          restoredFocus.focus({ preventScroll: true });
        }
      }
    }
    controller = createController({
      getAllowedCodes: function () { return environment().allowedCodes; },
      adapter: adapter,
      api: { read: function () { return request("cart.js"); }, write: function (payload) {
        var sectionIds = ["cart-icon-bubble"];
        var footer = doc.getElementById("main-cart-footer");
        if (footer && footer.dataset.id) sectionIds.push(footer.dataset.id);
        return request("cart/update.js", Object.assign({ sections: sectionIds }, payload));
      } },
      waitIdle: waitIdle,
      onDiagnostic: function (event, details) {
        diagnostic(event, Object.assign({}, details, { cachedCart: win.FGSECOMAPP && win.FGSECOMAPP.SHOPIFY_CART }));
      },
      onState: updateUI,
      onMutation: function (event) {
        diagnostic("mutation.complete", { reason: event.reason, state: event.state, cart: event.state.cart });
        // A reload could start native restoration against an unverified stale
        // cache. Keep the visible error and checkout gate until a safe retry.
        if (event.syncFailed) return;
        if (event.reason === "reconcile") {
          refreshNative(event.state.cart).catch(function () { controller.setFeedback("Kurvens visning kunne ikke opdateres. Genindlæs siden og kontrollér varerne."); });
          return;
        }
        // A full reload for the two explicit coupon actions keeps native cart
        // totals, shipping, gift UI and theme event bindings in sync. Automatic
        // reconciliation NEVER reloads, so a gift race cannot create a loop.
        if (reloadRequested) return;
        reloadRequested = true;
        try { win.sessionStorage.setItem(flashKey, JSON.stringify({ error: event.state.error, restore: !event.state.active, drawer: Boolean(doc.querySelector("cart-drawer.active")), focus: event.state.active ? "remove" : "input" })); } catch (error) {}
        diagnostic("page.before-reload", { reason: event.reason, state: event.state, restore: !event.state.active });
        win.location.reload();
      }
    });
    checkoutGate = createCheckoutGate({ document: doc, window: win,
      getState: controller.getState,
      getAdapterState: adapter.getState,
      onBlocked: function (message) {
        var activeDrawer = doc.querySelector("cart-drawer.active .rt-campaign-cart");
        var panel = activeDrawer || doc.querySelector("#main-cart-footer .rt-campaign-cart") || doc.querySelector(".rt-campaign-cart");
        if (!panel) return;
        panel.hidden = false;
        var status = panel.querySelector(".rt-campaign-cart__status");
        status.textContent = message;
        status.setAttribute("tabindex", "-1");
        status.focus({ preventScroll: false });
      }
    });
    function mount() {
      var hosts = doc.querySelectorAll(".cart-drawer__footer, #main-cart-footer .js-contents");
      hosts.forEach(function (host) {
        if (host.querySelector(".rt-campaign-cart")) return;
        var panel = doc.createElement("section");
        panel.className = "rt-campaign-cart";
        panel.setAttribute("aria-label", "Efterårskampagne");
        var inputId = "rt-campaign-code-" + (++panelCount);
        // No nested form: Dawn's main-cart footer can already be inside one.
        panel.innerHTML = '<label class="rt-campaign-cart__label" for="' + inputId + '">Rabatkode</label><div class="rt-campaign-cart__row"><input id="' + inputId + '" class="rt-campaign-cart__input" type="text" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="EFTERÅR15"><button class="rt-campaign-cart__button" data-rt-apply type="button">Anvend</button></div><p class="rt-campaign-cart__status" role="status" aria-live="polite" aria-atomic="true"></p><button class="rt-campaign-cart__remove" data-rt-remove type="button" hidden>Fjern EFTERÅR15 og vis gaver igen</button><p class="rt-campaign-cart__help">Fri fragt følger de normale betingelser.</p>';
        var input = panel.querySelector("input");
        var apply = function () { if (!controller.getState().pending) controller.apply(input.value); };
        panel.querySelector("[data-rt-apply]").addEventListener("click", apply);
        input.addEventListener("keydown", function (event) {
          if (event.key === "Enter") { event.preventDefault(); event.stopPropagation(); apply(); }
        });
        panel.querySelector("[data-rt-remove]").addEventListener("click", function () { if (!controller.getState().pending) controller.remove(); });
        host.prepend(panel);
        nodes.add(panel);
      });
      updateUI(controller.getState());
    }
    function start() {
      if (booted) return;
      booted = true;
      diagnostic("page.boot", { restore: restoreAfterBoot });
      mount();
      var untilExpiry = CAMPAIGN_END - Date.now() + 1;
      if (untilExpiry > 0 && untilExpiry <= 2147483647) expiryTimer = win.setTimeout(function () {
        updateUI(controller.getState());
        schedule();
      }, untilExpiry);
      observer = new win.MutationObserver(mount);
      observer.observe(doc.body, { childList: true, subtree: true });
      ["fg-app:end", "fg-gifts:updated", "fg-cart:auto-updated"].forEach(function (name) { listen(doc, name, schedule); });
      listen(win, "pageshow", schedule);
      listen(win, "focus", schedule);
      listen(doc, "visibilitychange", function () { if (doc.visibilityState === "visible") schedule(); });
      listen(doc, "click", function (event) { if (event.target.closest && event.target.closest("#cart-icon-bubble")) schedule(); });
      // Dawn declares this event constant with const, not always window.*.
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        unsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
      }
      controller.reconcile().then(function () {
        restoreIfNeeded();
        if (!flash) return;
        if (flash.drawer) {
          var drawer = doc.querySelector("cart-drawer");
          if (drawer && typeof drawer.open === "function") drawer.open();
        }
        mount();
        var panel = doc.querySelector(flash.drawer ? "cart-drawer .rt-campaign-cart" : ".rt-campaign-cart");
        if (panel) {
          if (flash.error) controller.setFeedback(flash.error);
          var focus = panel.querySelector(flash.focus === "remove" ? "[data-rt-remove]" : "input");
          if (focus && !focus.hidden) focus.focus();
        }
        flash = null;
      });
    }
    var handle = {
      controller: controller,
      adapter: adapter,
      destroy: function () {
        controller.destroy(); adapter.destroy();
        if (observer) observer.disconnect();
        if (scheduled) win.clearTimeout(scheduled);
        if (expiryTimer) win.clearTimeout(expiryTimer);
        checkoutGate.destroy();
        if (unsubscriber) unsubscriber();
        listeners.forEach(function (entry) { entry[0].removeEventListener(entry[1], entry[2]); });
        nodes.forEach(function (node) { node.remove(); });
        doc.documentElement.removeAttribute("data-retouch-campaign-active");
        delete win.__retouchCampaignCart;
      }
    };
    win.__retouchCampaignCart = handle;
    if (doc.readyState === "loading") listen(doc, "DOMContentLoaded", start);
    else start();
    return handle;
  }
  return Object.freeze({ normalize: normalize, previewDiagnosticsEnabled: previewDiagnosticsEnabled, createPreviewDiagnostics: createPreviewDiagnostics, campaignAvailable: campaignAvailable, campaignPanelVisible: campaignPanelVisible, checkoutGateState: checkoutGateState, createCheckoutGate: createCheckoutGate, storefrontEnvironment: storefrontEnvironment, activeCode: activeCode, existingCodes: existingCodes, giftUpdates: giftUpdates, hasUnresolvedGift: hasUnresolvedGift, applyPayload: applyPayload, removePayload: removePayload, observeNativeRestoration: observeNativeRestoration, completeRestoration: completeRestoration, createController: createController, boot: boot });
});
;
